import React, { useEffect, useState } from "react";
import { CloudSun, Droplets, Wind, MapPin, RefreshCw, CalendarDays } from "lucide-react";

export default function WeatherStation() {
  const [weather, setWeather] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch("https://api.open-meteo.com/v1/forecast?latitude=-32.7615&longitude=151.7470&current=temperature_2m,relative_humidity_2m,wind_speed_10m,weather_code&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max,wind_speed_10m_max&forecast_days=7&timezone=Australia%2FSydney")
      .then((res) => res.json())
      .then((data) => setWeather(data))
      .catch(() => setError("Weather unavailable"));
  }, []);

  if (error) return <div className="rounded-2xl bg-card border border-border p-5 shadow-sm">{error}</div>;

  if (!weather?.current || !weather?.daily) {
    return (
      <div className="rounded-2xl bg-card border border-border p-5 shadow-sm">
        <div className="flex items-center gap-2 text-muted-foreground">
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span>Loading weather station...</span>
        </div>
      </div>
    );
  }

  const c = weather.current;
  const d = weather.daily;

  return (
    <div className="rounded-2xl bg-gradient-to-br from-sky-600 to-blue-900 text-white p-6 shadow-lg w-full">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 text-white/90 text-sm">
            <MapPin className="h-4 w-4" />
            Raymond Terrace Weather Station
          </div>
          <h3 className="text-lg font-semibold mt-1">Live Conditions + 7 Day Forecast</h3>
        </div>
        <CloudSun className="h-9 w-9 text-white/90" />
      </div>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-5">
        <div>
          <div className="text-5xl font-bold">{Math.round(c.temperature_2m)}°C</div>
          <p className="text-xs text-white/70 mt-2">
            Updated: {new Date(c.time).toLocaleString("en-AU")}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm min-w-[280px]">
          <div className="rounded-2xl bg-white/15 p-5 text-center">
            <div className="flex items-center gap-2 text-white/80">
              <Droplets className="h-4 w-4" /> Humidity
            </div>
            <div className="text-xl font-semibold">{c.relative_humidity_2m}%</div>
          </div>

          <div className="rounded-2xl bg-white/15 p-5 text-center">
            <div className="flex items-center gap-2 text-white/80">
              <Wind className="h-4 w-4" /> Wind
            </div>
            <div className="text-xl font-semibold">{Math.round(c.wind_speed_10m)} km/h</div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/20 pt-4">
        <div className="flex items-center gap-2 text-sm font-semibold mb-3">
          <CalendarDays className="h-4 w-4" />
          7 Day Forecast
        </div>

        <div className="grid grid-cols-7 gap-4">
          {d.time.map((day, i) => (
            <div key={day} className="rounded-2xl bg-white/15 p-5 text-center">
              <div className="font-semibold text-sm">
                {new Date(day).toLocaleDateString("en-AU", { weekday: "short" })}
              </div>
              <div className="text-xs text-white/70">
                {new Date(day).toLocaleDateString("en-AU", { day: "2-digit", month: "short" })}
              </div>
              <div className="mt-2 text-lg font-bold">
                {Math.round(d.temperature_2m_max[i])}° / {Math.round(d.temperature_2m_min[i])}°
              </div>
              <div className="text-xs text-white/80 mt-1">
                Rain {d.precipitation_probability_max[i] ?? 0}%
              </div>
              <div className="text-xs text-white/80">
                Wind {Math.round(d.wind_speed_10m_max[i])} km/h
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
